import {
	AddAssessmentRequest,
	Assessment,
	AssessmentsRequest,
	AssessmentsResponse,
	AssessmentVulnerabilityData,
	AssessmentDataLog,
	EditAssessmentDataLogRequest,
	CreateCategoryRequest,
	ModifyCategoryRequest,
	ReorderCategoriesRequest,
	Ms365Assessment
} from "@/Contracts/Assessments";
import Axios, { AxiosResponse } from "axios";
import {
	postRequest,
	getRequest,
	putRequest,
	deleteRequest
} from "@/Utils/Service/BackendService";
import {
	ChildEntityHydrationParams,
	includeChildEntities,
	getTableQueryFilters,
	getQueryParamAuthDetails
} from "@/Contracts/Utils";
import { DeletedRecord } from "@/Contracts";
import { AssessmentEditRequest } from "@/Contracts";

const getAssessmentsUrl = "/v2/assessments";
const getOrEditAssessmentDetailsUrl = "/v2/assessments/{assessment_id}";
const addAssessmentUrl = "/v2/assessments";
const getManualAssessmentDefaultsUrl = "/v2/assessments/vulnerability_data";
const editAssessmentDataLogUrl =
	"/v2/assessments/{assessment_id}/logs/{assessment_data_log_id}";
const addAssessmentDataLogUrl = "/v2/assessments/{assessment_id}/logs";
const deleteAssessmentDataLogUrl =
	"/v2/assessments/{assessment_id}/logs/{assessment_data_log_id}";
const flushAssessmentToDocumentUrl =
	"/v2/assessments/{assessment_id}/flush/{entity_type}";
const resetRiskScoreUrl = "/v2/assessments/{assessment_id}/score/default";
const getCategoriesUrl = "/v2/assessments/{assessment_id}/category/";
const createCategoryUrl = "/v2/assessments/{assessment_id}/category/";
const updateCategoryUrl =
	"/v2/assessments/{assessment_id}/category/{issue_id}/";
const deleteCategoryUrl =
	"/v2/assessments/{assessment_id}/category_delete/{issue_id}/";
const reorderCategoriesUrl =
	"/v2/assessments/{assessment_id}/category/reorder/";

// Ms 365 Assessments
const getMs365AssmtsUrl = "/v2/automated-assessments/{client_id}/ms365/";
const insertMs365AssmtIntoDocumentUrl =
	"/v2/assessments/{assessment_or_qbr_id}/add-ms365-assessment/{ms365Assessment_id}/";
const previewMs365AssmtUrl =
	"/v2/automated-assessments/{client_id}/ms365/{ms365Assessment_id}";
const runMs365AssmtUrl = "/v2/automated-assessments/{client_id}/ms365/";
const disconnectMs365ClientUrl = "/v2/ms365/disconnect/{client_id}/";

export const addAssessment = async (
	addAssessmentRequest: AddAssessmentRequest
): Promise<Assessment> => {
	try {
		const response: AxiosResponse<Assessment> = await Axios(
			postRequest(
				addAssessmentUrl,
				addAssessmentRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editAssessmentDataLog = async (
	editAssessmentDataLogRequest: EditAssessmentDataLogRequest,
	assessmentId: string
): Promise<AssessmentDataLog> => {
	try {
		const response: AxiosResponse<AssessmentDataLog> = await Axios(
			putRequest(
				`${editAssessmentDataLogUrl
					.replace("{assessment_id}", assessmentId)
					.replace(
						"{assessment_data_log_id}",
						editAssessmentDataLogRequest.id
					)}?${includeChildEntities(
					new Array<ChildEntityHydrationParams>(
						ChildEntityHydrationParams.ASSESSMENT_BLOCKS
					)
				)}`,
				editAssessmentDataLogRequest,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const flushAssessmentToDocument = async (
	assessmentId: string,
	entityType: "document" | "template"
): Promise<void> => {
	try {
		const response: AxiosResponse<void> = await Axios(
			putRequest(
				`${flushAssessmentToDocumentUrl
					.replace("{assessment_id}", assessmentId)
					.replace("{entity_type}", entityType)}`,
				{},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const addAssessmentDataLog = async (
	assessmentDataLog: AssessmentDataLog,
	assessmentId: string
): Promise<AssessmentDataLog> => {
	try {
		const response: AxiosResponse<AssessmentDataLog> = await Axios(
			postRequest(
				`${addAssessmentDataLogUrl.replace(
					"{assessment_id}",
					assessmentId
				)}?${includeChildEntities(
					new Array<ChildEntityHydrationParams>(
						ChildEntityHydrationParams.ASSESSMENT_BLOCKS
					)
				)}`,
				assessmentDataLog,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getAssessmentDetails = async (
	assessmentId: string,
	appendQueryParamAuth: boolean
): Promise<Assessment> => {
	try {
		const includedChildEntities = includeChildEntities(
			new Array<ChildEntityHydrationParams>(
				ChildEntityHydrationParams.ASSESSMENT_LOGS,
				ChildEntityHydrationParams.ASSESSMENT_BLOCKS
			)
		);
		let url = getOrEditAssessmentDetailsUrl.replace(
			"{assessment_id}",
			assessmentId
		);

		const authQueryParam = getQueryParamAuthDetails();
		if (appendQueryParamAuth && !!authQueryParam) {
			url =
				url +
				"?" +
				authQueryParam.queryParam +
				"&" +
				includedChildEntities;
		} else {
			url = url + "?" + includedChildEntities;
		}
		const response: AxiosResponse<Assessment> = await Axios(
			getRequest(url, null, true, false, false)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const editAssessmentDetails = async (
	assessment: Assessment,
	assessmentData: AssessmentEditRequest
): Promise<Assessment> => {
	try {
		const response: AxiosResponse<Assessment> = await Axios(
			putRequest(
				`${getOrEditAssessmentDetailsUrl.replace(
					"{assessment_id}",
					assessment.id
				)}?${includeChildEntities(
					new Array<ChildEntityHydrationParams>(
						ChildEntityHydrationParams.ASSESSMENT_LOGS,
						ChildEntityHydrationParams.ASSESSMENT_BLOCKS
					)
				)}`,
				assessmentData,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const resetRiskScore = async (
	assessmentId: string
): Promise<Assessment> => {
	try {
		const response: AxiosResponse<Assessment> = await Axios(
			putRequest(
				`${resetRiskScoreUrl.replace(
					"{assessment_id}",
					assessmentId
				)}?${includeChildEntities(
					new Array<ChildEntityHydrationParams>(
						ChildEntityHydrationParams.ASSESSMENT_LOGS,
						ChildEntityHydrationParams.ASSESSMENT_BLOCKS
					)
				)}`,
				{},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getAssessments = async (
	assessmentsRequest: AssessmentsRequest
): Promise<AssessmentsResponse> => {
	try {
		const included_child_entities = includeChildEntities(
			assessmentsRequest.includeEntities
		);
		let queryParams = `${
			included_child_entities && included_child_entities.length > 0
				? included_child_entities
				: ""
		}`;
		if (assessmentsRequest.filters.length > 0) {
			const appliedFilters = getTableQueryFilters(
				assessmentsRequest.filters,
				false
			);
			queryParams = `${queryParams}${
				appliedFilters.length > 0 ? `&${appliedFilters}` : ""
			}`;
		}
		queryParams = `${queryParams}${
			assessmentsRequest.keywords &&
			assessmentsRequest.keywords.length > 0
				? `&q=${assessmentsRequest.keywords}`
				: ""
		}`;
		const response: AxiosResponse<AssessmentsResponse> = await Axios(
			getRequest(
				`${getAssessmentsUrl}${
					queryParams.length > 0 ? `?${queryParams}` : ""
				}`,
				null,
				true,
				true,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getManualAssessmentDefaults = async (): Promise<
	AssessmentVulnerabilityData[]
> => {
	try {
		const response: AxiosResponse<AssessmentVulnerabilityData[]> =
			await Axios(
				getRequest(
					getManualAssessmentDefaultsUrl,
					null,
					true,
					true,
					false
				)
			);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteAssessmentDataLog = async (
	assessmentId: string,
	asesssmentDataLogId: string
): Promise<DeletedRecord> => {
	try {
		const response: AxiosResponse<DeletedRecord> = await Axios(
			deleteRequest(
				deleteAssessmentDataLogUrl
					.replace("{assessment_id}", assessmentId)
					.replace("{assessment_data_log_id}", asesssmentDataLogId),
				null,
				true,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getCategories = async (
	assessmentId: string
): Promise<Assessment> => {
	try {
		const response: AxiosResponse<Assessment> = await Axios(
			getRequest(
				getCategoriesUrl.replace("{assessment_id}", assessmentId),
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const createCategory = async (
	createCategoryRequest: CreateCategoryRequest
): Promise<Assessment> => {
	try {
		const response: AxiosResponse<Assessment> = await Axios(
			postRequest(
				createCategoryUrl.replace(
					"{assessment_id}",
					createCategoryRequest.entityId
				),
				{
					issue_type: createCategoryRequest.issueType,
					cloud_assessment_type:
						createCategoryRequest.cloud_assessment_type
				},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const updateCategory = async (
	modifyCategoryRequest: ModifyCategoryRequest
): Promise<Assessment> => {
	try {
		const includedChilEntities = includeChildEntities(
			new Array<ChildEntityHydrationParams>(
				ChildEntityHydrationParams.ASSESSMENT_LOGS,
				ChildEntityHydrationParams.ASSESSMENT_BLOCKS
			)
		);
		const response: AxiosResponse<Assessment> = await Axios(
			putRequest(
				`${updateCategoryUrl
					.replace("{assessment_id}", modifyCategoryRequest.entityId)
					.replace(
						"{issue_id}",
						modifyCategoryRequest.issueId
					)}?${includedChilEntities}`,
				{
					issue_type: modifyCategoryRequest.issueType,
					cloud_assessment_type:
						modifyCategoryRequest.cloud_assessment_type,
					filter_on_status: modifyCategoryRequest.filterOnStatus
				},
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const deleteCategory = async (
	modifyCategoryRequest: ModifyCategoryRequest
): Promise<Assessment> => {
	try {
		const includedChilEntities = includeChildEntities(
			new Array<ChildEntityHydrationParams>(
				ChildEntityHydrationParams.ASSESSMENT_LOGS,
				ChildEntityHydrationParams.ASSESSMENT_BLOCKS
			)
		);
		const response: AxiosResponse<Assessment> = await Axios(
			putRequest(
				`${deleteCategoryUrl
					.replace("{assessment_id}", modifyCategoryRequest.entityId)
					.replace(
						"{issue_id}",
						modifyCategoryRequest.issueId
					)}?${includedChilEntities}`,
				{ issue_type: modifyCategoryRequest.issueType },
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const reorderCategories = async (
	reorderCategoriesRequest: ReorderCategoriesRequest
): Promise<Assessment> => {
	try {
		const includedChilEntities = includeChildEntities(
			new Array<ChildEntityHydrationParams>(
				ChildEntityHydrationParams.ASSESSMENT_BLOCKS
			)
		);
		const response: AxiosResponse<Assessment> = await Axios(
			putRequest(
				`${reorderCategoriesUrl.replace(
					"{assessment_id}",
					reorderCategoriesRequest.entityId
				)}?${includedChilEntities}`,
				{ categories: reorderCategoriesRequest.newCategoryList },
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const getMs365Assessments = async (
	clientId: string
): Promise<{ data: Ms365Assessment[] }> => {
	try {
		const url = getMs365AssmtsUrl.replace("{client_id}", clientId);
		const response = await Axios(getRequest(url, null, true, false, false));
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const previewMs365Assessment = async (
	clientId: string,
	ms365AssessmentId: string
): Promise<Ms365Assessment> => {
	try {
		const includedChildEntities = includeChildEntities(
			new Array<ChildEntityHydrationParams>(
				ChildEntityHydrationParams.BLOCKS_CURRENT_REVISION
			)
		);
		const response = await Axios(
			getRequest(
				`${previewMs365AssmtUrl
					.replace("{client_id}", clientId)
					.replace("{ms365Assessment_id}", ms365AssessmentId)}
					?${includedChildEntities}`,
				null,
				true,
				false,
				false
			)
		);
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const insertMs365AssessmentIntoDocument = async (
	entityId: string,
	ms365AssessmentId: string
): Promise<Assessment> => {
	try {
		const includedChilEntities = includeChildEntities(
			new Array<ChildEntityHydrationParams>(
				ChildEntityHydrationParams.ASSESSMENT_LOGS,
				ChildEntityHydrationParams.ASSESSMENT_BLOCKS
			)
		);
		const url = `${insertMs365AssmtIntoDocumentUrl
			.replace("{assessment_or_qbr_id}", entityId)
			.replace(
				"{ms365Assessment_id}",
				ms365AssessmentId
			)}?${includedChilEntities}`;
		const response = await Axios(postRequest(url, {}, true, false, false));
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const runMs365Assessment = async (
	clientId: string
): Promise<Ms365Assessment> => {
	try {
		const url = runMs365AssmtUrl.replace("{client_id}", clientId);
		const response = await Axios(postRequest(url, {}, true, false, false));
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};

export const disconnectMs365Client = async (clientId: string) => {
	try {
		const url = disconnectMs365ClientUrl.replace("{client_id}", clientId);
		const response = await Axios(postRequest(url, {}, true, false, false));
		return response.data;
	} catch (error) {
		return Promise.reject(error);
	}
};
